import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getAdminOverview } from "@/lib/admin.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatEuro } from "@/lib/salonturbo";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: AdminOverviewPage,
});

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card className="rounded-3xl">
      <CardContent className="p-5">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-2 text-2xl font-light">{value}</p>
        {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

function pct(part: number, total: number): string {
  if (!total) return "0 %";
  return `${Math.round((part / total) * 100)} %`;
}

function AdminOverviewPage() {
  const fetchOverview = useServerFn(getAdminOverview);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-overview"],
    queryFn: () => fetchOverview(),
  });

  if (isLoading || !data) {
    return (
      <div className="flex min-h-[30vh] items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  const monthLabel = (m: string) => {
    const [year, month] = m.split("-");
    return `${month}/${year?.slice(2)}`;
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric
          label="Salons"
          value={String(data.salons.total)}
          hint={`${data.salons.active} aktiv · ${data.salons.draft} in Einrichtung · ${data.salons.paused} pausiert`}
        />
        <Metric
          label="Neu (30 Tage)"
          value={String(data.salons.new_30d)}
          hint={`${data.salons.onboarded} haben die Einrichtung abgeschlossen`}
        />
        <Metric
          label="Inaktiv"
          value={String(data.inactive.no_boost_30d)}
          hint={`kein Boost seit 30 Tagen · ${data.inactive.never_boosted} noch nie geboostet`}
        />
        <Metric
          label="Ø Coins / Salon / Monat"
          value={String(data.avg_coins_per_salon_month)}
          hint="nur Monate mit Verbrauch"
        />
        <Metric
          label="Coins aufgeladen"
          value={String(data.coins.purchased)}
          hint={`${data.coins.refunded} erstattet · ${data.coins.adjustments} manuell`}
        />
        <Metric label="Coins verbraucht" value={String(data.coins.spent)} />
        <Metric label="Offenes Guthaben" value={`${data.coins.balance} Coins`} />
        <Metric
          label="Umsatz netto"
          value={formatEuro(data.revenue.net_cents)}
          hint={`brutto ${formatEuro(data.revenue.gross_cents)} · ${data.revenue.paid_count} Zahlungen`}
        />
        <Metric
          label="Umsatz laufender Monat"
          value={formatEuro(data.revenue.month_net_cents)}
          hint={`${data.revenue.pending_count} offene Zahlungen`}
        />
        <Metric
          label="Boost-Tage gelaufen"
          value={String(data.boosts.completed)}
          hint={`${data.boosts.upcoming} kommend · ${data.boosts.cancelled} storniert · ${data.boosts.failed} fehlgeschlagen`}
        />
        <Metric
          label="Kampagnen-Gesundheit"
          value={`${data.salons.without_campaign} ohne Kampagne`}
          hint={`${data.salons.campaign_error} mit Kampagnenfehler`}
        />
        <Metric
          label="Support"
          value={`${data.support.open} offen`}
          hint={`${data.support.in_progress} in Arbeit · ${data.support.closed} geschlossen`}
        />
      </div>

      <Card className="rounded-3xl">
        <CardHeader>
          <CardTitle className="text-lg font-light">Onboarding-Trichter</CardTitle>
          <CardDescription>Wie viele Salons kommen wie weit?</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-5">
          {[
            { label: "Registriert", value: data.funnel.registered },
            { label: "Salon-Daten", value: data.funnel.step_profile },
            { label: "Öffnungszeiten", value: data.funnel.step_hours },
            { label: "Einrichtung fertig", value: data.funnel.completed },
            { label: "Erster Boost", value: data.funnel.first_boost },
          ].map((step) => (
            <div key={step.label} className="rounded-2xl border p-4">
              <p className="text-xs text-muted-foreground">{step.label}</p>
              <p className="mt-1 text-xl font-light">{step.value}</p>
              <p className="text-xs text-muted-foreground">
                {pct(step.value, data.funnel.registered)} von allen
              </p>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle className="text-lg font-light">Anzeigen-Leistung</CardTitle>
            <CardDescription>Summe über alle Boost-Tage</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-2xl border p-4">
              <p className="text-xs text-muted-foreground">Impressionen</p>
              <p className="mt-1 text-xl font-light">{data.ads.impressions}</p>
            </div>
            <div className="rounded-2xl border p-4">
              <p className="text-xs text-muted-foreground">Klicks</p>
              <p className="mt-1 text-xl font-light">{data.ads.clicks}</p>
              <p className="text-xs text-muted-foreground">
                Klickrate {pct(data.ads.clicks, data.ads.impressions)}
              </p>
            </div>
            <div className="rounded-2xl border p-4">
              <p className="text-xs text-muted-foreground">Anzeigenkosten</p>
              <p className="mt-1 text-xl font-light">{formatEuro(data.ads.cost_cents)}</p>
            </div>
            <div className="rounded-2xl border p-4">
              <p className="text-xs text-muted-foreground">Conversions</p>
              <p className="mt-1 text-xl font-light">{Math.round(data.ads.conversions)}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-3xl">
          <CardHeader>
            <CardTitle className="text-lg font-light">Salon-Seiten</CardTitle>
            <CardDescription>Boost-Tage gegen normale Tage</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full min-w-[420px] text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="pb-2">Tag</th>
                  <th className="pb-2">Aufrufe</th>
                  <th className="pb-2">Anrufe</th>
                  <th className="pb-2">Buchungen</th>
                  <th className="pb-2">Anruf-Quote</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t">
                  <td className="py-2">Boost</td>
                  <td>{data.landing.views_boosted}</td>
                  <td>{data.landing.calls_boosted}</td>
                  <td>{data.landing.books_boosted}</td>
                  <td>{pct(data.landing.calls_boosted, data.landing.views_boosted)}</td>
                </tr>
                <tr className="border-t">
                  <td className="py-2">Normal</td>
                  <td>{data.landing.views_normal}</td>
                  <td>{data.landing.calls_normal}</td>
                  <td>{data.landing.books_normal}</td>
                  <td>{pct(data.landing.calls_normal, data.landing.views_normal)}</td>
                </tr>
                <tr className="border-t font-medium">
                  <td className="py-2">Gesamt</td>
                  <td>{data.landing.views}</td>
                  <td>{data.landing.calls}</td>
                  <td>{data.landing.books}</td>
                  <td>{pct(data.landing.calls, data.landing.views)}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-3xl">
        <CardHeader>
          <CardTitle className="text-lg font-light">Verlauf (12 Monate)</CardTitle>
          <CardDescription>Registrierungen, Coins und Umsatz pro Monat</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="pb-2">Monat</th>
                <th className="pb-2">Neu</th>
                <th className="pb-2">Coins gekauft</th>
                <th className="pb-2">Coins genutzt</th>
                <th className="pb-2">Boost-Tage</th>
                <th className="pb-2">Umsatz netto</th>
              </tr>
            </thead>
            <tbody>
              {data.months.map((month) => (
                <tr key={month.month} className="border-t">
                  <td className="py-2">{monthLabel(month.month)}</td>
                  <td>{month.signups}</td>
                  <td>{month.coins_purchased}</td>
                  <td>{month.coins_spent}</td>
                  <td>{month.boost_days}</td>
                  <td>{formatEuro(month.net_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
